import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { navigationClient } from '../lib/navigation';
import EventSection from '../components/event/EventSection';
import ListSection from '../components/list/ListSection';
import StandardSection from '../components/standard/StandardSection';
import EventApiSection from '../components/event-api/EventApiSection';
import ReviewSection from '../components/review/ReviewSection';
import { LoadingIndicator } from '../components/shared/LoadingIndicator';
import { logger } from '../lib/logger';

export const SectionRouter: React.FC = () => {
  // Extraire les paramètres de l'URL
  const { slug, name, item } = useParams<{ slug: string; name?: string; item?: string }>();
  const navigate = useNavigate();

  // Charger les données de navigation
  const { data: navigation, isLoading, error } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => {
      logger.debug('SectionRouter: Loading navigation data', {
        category: 'Routing',
        data: {
          slug,
          name,
          item,
          timestamp: new Date().toISOString()
        }
      });
      return navigationClient.getNavigation();
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  useEffect(() => {
    logger.debug('SectionRouter mounted', {
      category: 'Routing',
      data: {
        slug,
        name,
        item,
        hasNavigation: !!navigation,
        isLoading,
        error: error ? String(error) : null,
        timestamp: new Date().toISOString()
      }
    });

    return () => {
      logger.debug('SectionRouter unmounted', {
        category: 'Routing',
        data: {
          slug,
          name,
          item,
          timestamp: new Date().toISOString()
        }
      });
    };
  }, [slug, name, item, navigation, isLoading, error]);

  if (isLoading) {
    return <LoadingIndicator id="section-router-loader" />;
  }

  if (error || !navigation) {
    logger.error('Error loading navigation data', {
      category: 'Routing',
      data: {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        slug,
        name,
        item,
        timestamp: new Date().toISOString()
      }
    });
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement de la navigation</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  // Trouver la section correspondante
  const section = navigation.find(item => {
    const itemSlug = navigationClient.generateSlug(item.names.fr);
    return itemSlug === slug;
  });

  if (!section) {
    logger.warn('Section not found', {
      category: 'Routing',
      data: {
        slug,
        name,
        item,
        availableSections: navigation.map(item => ({
          id: item.id,
          slug: navigationClient.generateSlug(item.names.fr),
          type: item.type_rubrique
        })),
        timestamp: new Date().toISOString()
      }
    });

    // Rediriger vers la page d'accueil
    navigate('/', { replace: true });
    return null;
  }

  logger.debug('Section found, rendering component', {
    category: 'Routing',
    data: {
      sectionId: section.id,
      sectionType: section.type_rubrique,
      sectionName: section.names.fr,
      hasItemParam: !!name,
      itemParam: name,
      subItemParam: item,
      timestamp: new Date().toISOString()
    }
  });

  switch (section.type_rubrique) {
    case 'event':
      return <EventSection />;
    case 'event-api':
      return <EventApiSection />;
    case 'liste':
      return <ListSection />;
    case 'standard':
      return <StandardSection />;
    case 'review':
      return <ReviewSection />;
    default:
      logger.warn('Unknown section type', {
        category: 'Routing',
        data: {
          type: section.type_rubrique,
          slug,
          name,
          timestamp: new Date().toISOString()
        }
      });
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <h2 className="text-xl font-semibold mb-2">Type de section non pris en charge</h2>
          <p className="text-center mb-4">Le type de section "{section.type_rubrique}" n'est pas reconnu.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      );
  }
};